/**
 * The hand-authored Block 4 map: a small plaza with a walled interior room,
 * one doorway, a pond, and three NPCs with distinct facings so the C-zone
 * rule is visible. 26×16 tiles — larger than the 20×12 camera on purpose,
 * so camera scrolling and bounds-clamping are exercised.
 *
 * Authored as characters for readability. Legend:
 *   g grass · r road · s sidewalk · w water · # wall · d door · . interior
 */
import type { TileGrid, TileKind } from '@logic/grid.js';
import type { NpcPlacement } from '@logic/interaction.js';

const LEGEND: Record<string, TileKind> = {
  g: 'grass',
  r: 'road',
  s: 'sidewalk',
  w: 'water',
  '#': 'wall',
  d: 'door',
  '.': 'interior',
};

// prettier-ignore
const ROWS = [
  '##########################',
  '#ggggggggsrrsgggggggggggg#',
  '#ggggggggsrrsgggwwwwggggg#',
  '#ggggggggsrrsgggwwwwggggg#',
  '#ggggggggsrrsgggggggggggg#',
  '#sssssssssrrsssssssssssss#',
  '#rrrrrrrrrrrrrrrrrrrrrrrr#',
  '#sssssssssrrsssssssssssss#',
  '#ggggggggsrrsggg######ggg#',
  '#ggggggggsrrsggg#....#ggg#',
  '#ggggggggsrrsggg#....#ggg#',
  '#ggggggggsrrsggg#....#ggg#',
  '#ggggggggsrrsggg##d###ggg#',
  '#ggggggggsrrsgggggggggggg#',
  '#ggggggggsrrsgggggggggggg#',
  '##########################',
];

export function firstRoom(): TileGrid {
  const tiles = ROWS.map((row) =>
    [...row].map((ch) => {
      const kind = LEGEND[ch];
      if (!kind) throw new Error(`first-room: unknown tile character "${ch}"`);
      return kind;
    }),
  );
  const cols = tiles[0]?.length ?? 0;
  if (tiles.some((r) => r.length !== cols)) {
    throw new Error('first-room: ragged rows — every row must be the same width');
  }
  return { cols, rows: tiles.length, tiles };
}

/**
 * Three NPCs, three facings: approach rules become obvious by walking around
 * them. Static with fixed facing (locked decision).
 */
export const FIRST_ROOM_NPCS: readonly NpcPlacement[] = [
  { id: 'npc_plaza', col: 6, row: 4, facing: 'down' },
  { id: 'npc_pond', col: 19, row: 4, facing: 'left' },
  { id: 'npc_house', col: 18, row: 10, facing: 'down' }, // inside the room
];

/** Player spawn: on the road, south of the plaza. */
export const FIRST_ROOM_SPAWN = { col: 11, row: 13 };
